<template>
    <div
        ref="wrap"
        class="mentionable-wrap"
        @compositionstart="handleCompositionStart"
        @compositionend="handleCompositionEnd"
        @input="handleInput"
        @keydown.capture="handleKeyDown"
    >
        <Poptip
            :model-value="!!atwho"
            class="mentionable-popup-wrapper--anchor"
            popper-class="mentionable-popup-wrapper"
            transfer
        >
            <template #content>
                <div class="mentionable-popup" :class="poptipClass" @click.stop>
                    <slot name="list-header" />

                    <ul v-if="matched.length" class="mentionable-list">
                        <li
                            v-for="(item, index) in matched"
                            :key="index"
                            :ref="index === currentIndex ? 'cur' : undefined"
                            class="mentionable-list-item"
                            :class="{ 'mentionable-list-item--current': index === currentIndex }"
                            :data-index="index"
                            @mouseenter="selectByMouse"
                            @click="handleItemClick"
                        >
                            <slot name="item" :item="item">
                                <span v-if="item" v-text="item.value" />
                            </slot>
                        </li>
                    </ul>

                    <slot name="list-footer">
                        <div class="mentionable-list-footer">
                            <strong class="link-like" @click="insertAtCampaign">@campaign</strong>
                            will notify everyone collaborating on this campaign
                        </div>
                    </slot>
                </div>
            </template>
        </Poptip>

        <slot />
    </div>
</template>

<script lang="ts">
import { defineComponent, type PropType } from "vue";

/*
 * This component is a modified version of At component from vue-at library.
 * Due to the template overwriting limitations these has been ported and used directly
 * https://github.com/fritx/vue-at
 * https://fritx.github.io/vue-at/#/en/quickstart
 */

function scrollIntoView(el: HTMLLIElement, scrollParent: HTMLElement) {
    const diff = el.offsetTop - scrollParent.scrollTop;

    if (diff < 0 || diff > scrollParent.offsetHeight - el.offsetHeight) {
        scrollParent = scrollParent || el.parentElement;
        scrollParent.scrollTop = el.offsetTop;
    }
}

function applyRange(range: Range) {
    const selection = window.getSelection();

    if (!selection) {
        return;
    }

    selection.removeAllRanges();
    selection.addRange(range);
}

function getRange() {
    const selection = window.getSelection();

    if (!selection?.rangeCount || selection.rangeCount < 1) {
        return null;
    }

    return selection.getRangeAt(0);
}

function getLastAtIndex(text: string) {
    return text.lastIndexOf("@");
}

function closest(el: Element, predicate: (el: Element) => string | null) {
    if (predicate(el)) {
        return el;
    }

    if (!el.parentNode) {
        return null;
    }

    return closest(el.parentNode as Element, predicate);
}

function getPrecedingRange() {
    const r = getRange();

    if (!r) {
        return;
    }

    const range = r.cloneRange();
    range.collapse(true);
    range.setStart(range.endContainer, 0);

    return range;
}

type Mention = {
    alias: boolean;
    label: string;
    value: string;
};

export default defineComponent({
    name: "HoxMentionable",

    props: {
        modelValue: {
            type: String,
            required: true
        },

        members: {
            type: Array as PropType<Mention[]>,
            required: true
        },

        poptipWidth: {
            default: "normal",
            validator(value: string) {
                return ["normal", "wide"].includes(value);
            }
        }
    },

    emits: ["input", "insert"],

    data() {
        return {
            bindsValue: this.modelValue != null,
            chunk: "",
            poptipClass: "",
            hasComposition: false,
            matched: [] as Mention[],
            currentIndex: null as number | null,
            atwho: null as {
                range: Range;
                offset: number;
                x: number;
                y: number;
            } | null
        };
    },

    computed: {
        currentItem() {
            if (this.matched.length && this.currentIndex !== null) {
                return this.matched[this.currentIndex];
            }

            return {
                alias: false,
                label: "",
                value: ""
            };
        }
    },

    watch: {
        currentIndex: function (index) {
            if (index === null) {
                return;
            }

            this.$nextTick(this.scrollToCur);
        },

        members() {
            this.handleInput();
        },

        modelValue(value: string) {
            if (!this.bindsValue) {
                return;
            }

            this.handleValueUpdate(value);
        }
    },

    mounted() {
        if (this.bindsValue) {
            this.handleValueUpdate(this.modelValue);
        }

        this.poptipClass = `mentionable-popup--${this.poptipWidth}`;
    },

    methods: {
        deleteMatch: (name: string, chunk: string) => chunk === name + " ",
        filterMatch: (name: string, chunk: string) => name.toLowerCase().indexOf(chunk.toLowerCase()) > -1,

        handleValueUpdate(value: string) {
            const el = this.$el.querySelector("[contenteditable]");

            if (value === el.innerHTML) {
                return;
            }

            const ev = new Event("input", { bubbles: true });

            el.innerHTML = value;
            el.dispatchEvent(ev);
        },

        handleItemClick(e: MouseEvent) {
            this.selectByMouse(e);
            this.insertItem();

            e.stopPropagation();
            e.preventDefault();
        },

        handleDelete(e: KeyboardEvent) {
            const range = getPrecedingRange();

            if (!range) {
                return;
            }

            if (range.endOffset > 0) {
                let a = (range.endContainer.childNodes[range.endOffset] ||
                    range.endContainer.childNodes[range.endOffset - 1]) as HTMLElement;

                if (!a || (a.nodeType === Node.TEXT_NODE && !/^\s?$/.test(a.data))) {
                    return;
                }

                if (a.nodeType === Node.TEXT_NODE) {
                    if (a.previousSibling) {
                        a = a.previousSibling as HTMLElement;
                    }
                } else if (a.previousElementSibling) {
                    a = a.previousElementSibling as HTMLElement;
                }

                const ch = Array.from(a.childNodes) as HTMLElement[];
                ch.reverse().unshift(a);

                const last = ch.find(c => c?.getAttribute("data-at-embedded") != null);

                if (!last) {
                    return;
                }

                e.preventDefault();
                e.stopPropagation();
                const r = getRange();

                if (!r) {
                    return;
                }

                r.setStartBefore(last);
                r.deleteContents();
                applyRange(r);

                this.handleInput();

                return;
            }

            const text = range.toString();
            const index = getLastAtIndex(text);

            if (index < 0) {
                return;
            }

            const chunk = text.slice(index + 1);
            const has = this.members.some(v => this.deleteMatch(v.value, chunk));

            if (!has) {
                return;
            }

            e.preventDefault();
            e.stopPropagation();

            const r = getRange();

            if (!r) {
                return;
            }

            r.setStart(r.endContainer, index);
            r.deleteContents();
            applyRange(r);

            this.handleInput();
        },

        handleKeyDown(e: KeyboardEvent) {
            if (this.atwho) {
                if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                    if (!(e.metaKey || e.ctrlKey)) {
                        e.preventDefault();
                        e.stopPropagation();
                        this.selectByKeyboard(e.key === "ArrowUp");
                    }

                    return;
                }

                if (e.key === "Enter") {
                    this.insertItem();
                    e.preventDefault();
                    e.stopPropagation();

                    return;
                }

                if (e.key === "Escape") {
                    this.closePanel();

                    return;
                }
            }

            // 为了兼容ie ie9~11 editable无input事件 只能靠keydown触发 textarea正常
            // 另 ie9 textarea的delete不触发input
            const isValid = (e.keyCode >= 48 && e.keyCode <= 90) || e.key === "Backspace";

            if (isValid) {
                setTimeout(this.handleInput, 50);
            }

            if (e.key === "Backspace") {
                this.handleDelete(e);
            }
        },

        // compositionStart -> input -> compositionEnd
        handleCompositionStart() {
            this.hasComposition = true;
        },

        handleCompositionEnd() {
            this.hasComposition = false;
            this.handleInput();
        },

        handleInput() {
            if (this.hasComposition) {
                return;
            }

            const el = this.$el.querySelector("[contenteditable]");
            this.$emit("input", el.innerHTML);

            const precedingRange = getPrecedingRange();

            if (!precedingRange) {
                return;
            }

            const text = precedingRange.toString();
            const index = getLastAtIndex(text);

            // if current text doesn't contain "@" close suggestions panel
            if (index < 0) {
                this.closePanel();

                return;
            }

            const prev = text[index - 1];

            const chunk = text.slice(index + 1, text.length);
            this.chunk = chunk;

            // if previous character is a letter or
            // if current chunk ends with a space or
            // if current chunk starts with a space
            if (/^[a-z0-9]$/i.test(prev) || /\s/.test(chunk) || /^\s/.test(chunk)) {
                this.closePanel();
                return;
            }

            this.matched = this.members.filter(v => this.filterMatch(v.value, chunk));

            if (!this.matched.length) {
                this.closePanel();

                return;
            }

            const fn = () => {
                const r = precedingRange.cloneRange();
                r.setStart(r.endContainer, index + 1);
                const rect = r.getClientRects()[0];

                this.atwho = {
                    range: precedingRange,
                    offset: index,
                    x: rect.left,
                    y: rect.top - 4
                };

                this.currentIndex = 0;
            };

            if (this.atwho) {
                fn();
            } else {
                setTimeout(fn, 10);
            }
        },

        closePanel() {
            this.atwho = null;
            this.currentIndex = null;
            this.matched = [];
        },

        scrollToCur() {
            if (!this.$refs.cur) {
                return;
            }

            const curEl = (this.$refs.cur as HTMLLIElement[])[0];

            if (!curEl) {
                return;
            }

            const scrollParent = curEl.parentElement?.parentElement; // .mentionable-popup

            if (!scrollParent) {
                return;
            }

            scrollIntoView(curEl, scrollParent);
        },

        selectByMouse(e: MouseEvent) {
            if (!e.target || !this.atwho) {
                return;
            }

            const el = closest(e.target as Element, d => d.getAttribute("data-index"));

            if (!el) {
                return;
            }

            this.currentIndex = +(el.getAttribute("data-index") as string);
        },

        selectByKeyboard(up: boolean) {
            if (!this.atwho || this.currentIndex === null) {
                return;
            }

            const offset = up ? -1 : 1;
            this.currentIndex = (this.currentIndex + offset + this.matched.length) % this.matched.length;
        },

        insertHtml(text: string) {
            if (!this.atwho) {
                return;
            }

            const { range } = this.atwho;
            const r = range.cloneRange();
            const content = range.toString();
            const index = getLastAtIndex(content);

            r.setStart(r.endContainer, index);

            applyRange(r);
            applyRange(r);

            r.deleteContents();
            const node = r.endContainer;
            const newElement = document.createElement("span");

            const mentionTag = document.createElement("span");
            mentionTag.className = "mentionable-tag";
            mentionTag.textContent = text;

            newElement.appendChild(document.createTextNode(" "));
            newElement.appendChild(mentionTag);
            newElement.appendChild(document.createTextNode(" "));
            newElement.setAttribute("data-at-embedded", "");
            newElement.setAttribute("contenteditable", "false");

            if (node.nodeType === Node.TEXT_NODE) {
                const cut = r.endOffset;
                const secondPart = (node as Text).splitText(cut);

                node.parentNode?.insertBefore(newElement, secondPart);

                r.setEndBefore(secondPart);
            } else {
                const t = document.createTextNode(" ");
                r.insertNode(newElement);
                r.setEndAfter(newElement);
                r.insertNode(t);
                r.setEndAfter(t);
            }

            r.collapse(false); // 参数在IE下必传
            applyRange(r);
        },

        insertItem() {
            if (!this.atwho || this.currentIndex === null) {
                return;
            }

            this.insertHtml(`@${this.currentItem.value}`);
            this.$emit("insert");
            this.handleInput();
        },

        insertAtCampaign() {
            if (!this.atwho) {
                return;
            }

            this.insertHtml("@campaign");
            this.$emit("insert");
            this.handleInput();
        }
    }
});
</script>

<style lang="scss">
@use "@style/variables" as *;

.mentionable {
    &-wrap {
        position: relative;
    }

    &-panel {
        position: absolute;
        font-size: $font-size-small;
    }

    &-inner {
        position: relative;
    }
}

.mentionable-list {
    list-style: none;
    max-height: 200px;
    overflow-y: auto;
    padding: 0;
    margin: 0;

    &-header {
        border-bottom: 1px solid $grey3;
        padding: $spacing-small 20px $spacing-small;
    }

    &-footer {
        border-top: 1px solid $grey3;
        padding: $spacing-small 20px $spacing-small;
    }
}

.mentionable-list-item {
    box-sizing: border-box;
    padding: 8px 20px;
    white-space: nowrap;
    display: flex;
    align-items: center;
    border-bottom: 1px solid $grey1;

    span {
        overflow: hidden;
        text-overflow: ellipsis;
    }

    &--current {
        background: $grey1;
    }
}

.mentionable-popup {
    z-index: 11110 !important; //todo: check
    border-radius: 6px;
    box-shadow: 0 0 10px 0 rgba(101, 111, 122, 0.5);
    position: absolute;
    bottom: 0;
    left: -0.8em;
    cursor: default;
    background-color: $white;
    max-height: 300px;
    width: 335px;
    overflow-y: auto;
    white-space: normal;
    margin-right: 20px;

    &--wide {
        width: 450px;
    }

    &::-webkit-scrollbar {
        width: 11px;
        height: 11px;
    }

    &::-webkit-scrollbar-track {
        background-color: #f5f5f5;
    }

    &::-webkit-scrollbar-thumb {
        min-height: 36px;
        border: 2px solid transparent;
        border-top: 3px solid transparent;
        border-bottom: 3px solid transparent;
        background-clip: padding-box;
        border-radius: 7px;
        background-color: #c4c4c4;
    }
}

.mentionable-usage-description {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: center;
    padding: 20px $spacing-large;
    text-align: center;
}

.mentionable-popup-wrapper {
    .ivu-poptip-body {
        padding: 0;
    }

    &--anchor {
        height: 0;
        padding: 0;
        margin: 0;
    }
}
</style>
